import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const PIE_COLORS = ['#16a34a', '#dc2626', '#3b82f6', '#f59e0b', '#8b5cf6', '#0ea5e9', '#ec4899', '#14b8a6', '#84cc16', '#f97316'];

function fmtMoney(n) {
  return `₹ ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtMoneyShort(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 10000000) return '₹' + (v / 10000000).toFixed(1) + 'Cr';
  if (Math.abs(v) >= 100000) return '₹' + (v / 100000).toFixed(1) + 'L';
  if (Math.abs(v) >= 1000) return '₹' + (v / 1000).toFixed(0) + 'k';
  return '₹' + v.toFixed(0);
}

function PmsExpenseDashboard({ token, selectedProperty }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';

  // Filters
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [quickRange, setQuickRange] = useState('this-month');

  // Data
  const [badges, setBadges] = useState({ pending_approval_count: 0, anomaly_count: 0, ai_features_enabled: 0 });
  const [pnl, setPnl] = useState(null);
  const [expensesInRange, setExpensesInRange] = useState([]);
  const [recent, setRecent] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty?.chain_id, selectedProperty?.id]);

  // Quick range presets
  const applyQuickRange = (key) => {
    setQuickRange(key);
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = d.getMonth();
    if (key === 'today') {
      setFrom(today); setTo(today);
    } else if (key === 'this-month') {
      setFrom(monthStart); setTo(today);
    } else if (key === 'last-month') {
      const lm = new Date(yyyy, mm - 1, 1);
      const lmEnd = new Date(yyyy, mm, 0);
      setFrom(lm.toISOString().slice(0, 10));
      setTo(lmEnd.toISOString().slice(0, 10));
    } else if (key === 'this-year') {
      setFrom(`${yyyy}-01-01`); setTo(today);
    } else if (key === 'last-3-months') {
      const ld = new Date(yyyy, mm - 2, 1);
      setFrom(ld.toISOString().slice(0, 10)); setTo(today);
    }
  };

  // Main data load — refires when filters change
  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) {
      setRecent([]); setExpensesInRange([]); setPnl(null);
      setBadges({ pending_approval_count: 0, anomaly_count: 0, ai_features_enabled: 0 });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const qsRange = `from=${from}&to=${to}` + (departmentFilter ? `&department_id=${departmentFilter}` : '');
      const [badgesRes, pnlRes, expRes, recentRes, catRes, deptRes] = await Promise.all([
        fetch('/api/v1/expense/dashboard/badges', { headers: headers() }),
        fetch(`/api/v1/expense/reports/property-pnl?from=${from}&to=${to}`, { headers: headers() }),
        fetch(`/api/v1/expense/?${qsRange}&limit=2000`, { headers: headers() }),
        fetch('/api/v1/expense/?limit=10', { headers: headers() }),
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() }),
      ]);
      const [badgesData, pnlData, expData, recentData, catData, deptData] = await Promise.all([
        badgesRes.json(), pnlRes.json(), expRes.json(), recentRes.json(), catRes.json(), deptRes.json()
      ]);
      if (badgesRes.ok) setBadges(badgesData);
      if (pnlRes.ok) setPnl(pnlData);
      if (expRes.ok) setExpensesInRange(expData.rows || []);
      if (recentRes.ok) setRecent(recentData.rows || []);
      if (catRes.ok) setCategories(catData.rows || []);
      if (deptRes.ok) setDepartments(deptData.rows || []);
    } catch (e) {
      setError('Network error loading dashboard');
    } finally {
      setLoading(false);
    }
  }, [headers, selectedProperty?.id, from, to, departmentFilter]);

  // 6-month trend — pull expenses for the last 6 month-buckets, group by month
  const loadTrend = useCallback(async () => {
    if (!selectedProperty?.id) { setTrendData([]); return; }
    try {
      const now = new Date();
      const trendStart = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      const fromStr = trendStart.toISOString().slice(0, 10);
      const toStr = today;
      const res = await fetch(`/api/v1/expense/?from=${fromStr}&to=${toStr}&limit=5000`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) return;
      const byMonth = {};
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        byMonth[k] = 0;
      }
      (data.rows || []).forEach(r => {
        if (!r.expense_date) return;
        const k = r.expense_date.slice(0, 7);
        if (k in byMonth && ['APPROVED', 'POSTED', 'PAID'].includes(r.status)) {
          byMonth[k] += Number(r.total_amount || 0);
        }
      });
      setTrendData(Object.entries(byMonth).map(([month, expense]) => ({ month, expense })));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headers, selectedProperty?.id]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadTrend(); }, [loadTrend]);

  // ─── Derived values ────────────────────────────────────────────────────────
  const revenue = pnl?.totals?.revenue || 0;
  const expense = pnl?.totals?.expense || 0;
  const netProfit = revenue - expense;
  const margin = revenue ? (netProfit / revenue) * 100 : 0;

  const todayTotal = useMemo(
    () => expensesInRange
      .filter(r => r.expense_date === today && ['APPROVED', 'POSTED', 'PAID'].includes(r.status))
      .reduce((s, r) => s + Number(r.total_amount || 0), 0),
    [expensesInRange, today]
  );
  const todayCount = useMemo(
    () => expensesInRange.filter(r => r.expense_date === today).length,
    [expensesInRange, today]
  );

  // Department breakdown (top 10)
  const deptBreakdown = useMemo(() => {
    const map = {};
    expensesInRange
      .filter(r => ['APPROVED', 'POSTED', 'PAID'].includes(r.status))
      .forEach(r => {
        const id = r.department_id;
        map[id] = (map[id] || 0) + Number(r.total_amount || 0);
      });
    return Object.entries(map)
      .map(([id, amount]) => ({
        name: departments.find(d => d.department_id === Number(id))?.dept_name || '— Unassigned —',
        amount
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);
  }, [expensesInRange, departments]);

  const revVsExpData = [
    { name: 'Revenue', value: revenue },
    { name: 'Expense', value: expense },
  ].filter(d => d.value > 0);

  const tile = (label, value, accent, sub) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}`, borderRadius: 8, padding: '0.9rem 1.1rem', minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ color: '#6b7280', fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 700, color: '#111827', marginTop: 4 }}>{value}</div>
      {sub && <div style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const chartCard = (title, content, height = 280) => (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem', flex: '1 1 320px', minWidth: 0 }}>
      <h4 style={{ margin: '0 0 0.7rem 0', fontSize: '0.9rem', color: '#111827', fontWeight: 600 }}>{title}</h4>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">{content}</ResponsiveContainer>
      </div>
    </div>
  );

  const statusColor = (s) => ({
    DRAFT: '#9ca3af', SUBMITTED: '#3b82f6', APPROVED: '#10b981',
    POSTED: '#0ea5e9', PAID: '#16a34a', REJECTED: '#ef4444', CANCELLED: '#ef4444'
  })[s] || '#6b7280';

  const inputStyle = { padding: '0.4rem 0.55rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.82rem', background: '#fff' };
  const monthYearVal = from.slice(0, 7);

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '1rem' }}>Expense Dashboard</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property from Hotel Config first.</div>}
      {error && <div className="error-banner">{error}</div>}

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '0.8rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end' }}>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.74rem', color: '#6b7280' }}>
            <span>From</span>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setQuickRange(''); }} style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.74rem', color: '#6b7280' }}>
            <span>To</span>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setQuickRange(''); }} style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.74rem', color: '#6b7280' }}>
            <span>Month / Year</span>
            <input type="month" value={monthYearVal} onChange={e => {
              const v = e.target.value;
              if (!v) return;
              const [yyyy, mm] = v.split('-').map(Number);
              const start = new Date(yyyy, mm - 1, 1).toISOString().slice(0, 10);
              const end = new Date(yyyy, mm, 0).toISOString().slice(0, 10);
              setFrom(start); setTo(end); setQuickRange('');
            }} style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.74rem', color: '#6b7280', minWidth: 160 }}>
            <span>Department</span>
            <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} style={inputStyle}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.dept_name}</option>)}
            </select>
          </label>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginLeft: 'auto' }}>
            {[['today','Today'],['this-month','This Month'],['last-month','Last Month'],['last-3-months','Last 3M'],['this-year','This Year']].map(([k, label]) => (
              <button key={k} onClick={() => applyQuickRange(k)}
                style={{
                  padding: '0.3rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 999,
                  background: quickRange === k ? '#2563eb' : '#fff',
                  color: quickRange === k ? '#fff' : '#374151',
                  fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600
                }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards row 1: Revenue / Expense / Net Profit / Profit % */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        {tile('Revenue',     fmtMoney(revenue),  '#16a34a', 'Rooms · F&B · Banquet · Other')}
        {tile('Expense',     fmtMoney(expense),  '#dc2626', 'COGS + OPEX (net of ITC)')}
        {tile('Net Profit',  fmtMoney(netProfit), netProfit >= 0 ? '#16a34a' : '#dc2626', netProfit >= 0 ? 'Positive' : 'Loss')}
        {tile('Profit %',    `${margin.toFixed(1)}%`, netProfit >= 0 ? '#16a34a' : '#dc2626', 'Net profit ÷ revenue')}
      </div>

      {/* Existing badge tiles row */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        {tile("Today's Expenses", fmtMoney(todayTotal), '#3b82f6', `${todayCount} entries`)}
        {tile('Pending Approval', badges.pending_approval_count, '#f59e0b', 'Awaiting action')}
        {tile('Open Anomalies',   badges.anomaly_count,         '#ef4444', 'Review queue')}
        {tile('AI Features ON',   badges.ai_features_enabled,   '#8b5cf6', 'of 5 available')}
      </div>

      {/* Charts row */}
      <div style={{ display: 'flex', gap: '0.7rem', flexWrap: 'wrap', marginBottom: '0.8rem' }}>
        {chartCard('Revenue vs Expense', (
          revVsExpData.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', paddingTop: '6rem', fontSize: '0.85rem' }}>No revenue or expense data in range</div>
          ) : (
            <PieChart>
              <Pie data={revVsExpData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(d) => `${d.name}: ${fmtMoneyShort(d.value)}`}>
                {revVsExpData.map((d, i) => <Cell key={i} fill={d.name === 'Revenue' ? '#16a34a' : '#dc2626'} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Legend />
            </PieChart>
          )
        ))}

        {chartCard('Department Expense Breakdown', (
          deptBreakdown.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', paddingTop: '6rem', fontSize: '0.85rem' }}>No expense data in range</div>
          ) : (
            <BarChart data={deptBreakdown} margin={{ top: 5, right: 10, left: 0, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} style={{ fontSize: '0.7rem' }} />
              <YAxis tickFormatter={fmtMoneyShort} style={{ fontSize: '0.7rem' }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="amount" fill="#3b82f6">
                {deptBreakdown.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Bar>
            </BarChart>
          )
        ))}

        {chartCard('Monthly Expense Trend (6 months)', (
          trendData.length === 0 ? (
            <div style={{ color: '#9ca3af', textAlign: 'center', paddingTop: '6rem', fontSize: '0.85rem' }}>Loading…</div>
          ) : (
            <LineChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" style={{ fontSize: '0.7rem' }} />
              <YAxis tickFormatter={fmtMoneyShort} style={{ fontSize: '0.7rem' }} />
              <Tooltip formatter={(v) => fmtMoney(v)} />
              <Line type="monotone" dataKey="expense" stroke="#dc2626" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          )
        ))}
      </div>

      {/* Recent Expenses */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
          <h3 style={{ margin: 0, color: '#111827' }}>Recent Expenses</h3>
          <button onClick={loadData} disabled={loading} style={{ padding: '0.4rem 0.8rem', border: '1px solid #d1d5db', borderRadius: 6, background: '#f9fafb', cursor: 'pointer' }}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {recent.length === 0 ? (
          <div style={{ color: '#6b7280', padding: '1rem', textAlign: 'center' }}>No expenses recorded yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>Expense No</th>
                  <th style={{ padding: '0.5rem' }}>Date</th>
                  <th style={{ padding: '0.5rem' }}>Category</th>
                  <th style={{ padding: '0.5rem' }}>Department</th>
                  <th style={{ padding: '0.5rem' }}>Payee</th>
                  <th style={{ padding: '0.5rem' }}>Mode</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '0.5rem' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(r => {
                  const catName = categories.find(c => c.category_id === r.category_id)?.category_name || '—';
                  const deptName = departments.find(d => d.department_id === r.department_id)?.dept_name || '—';
                  return (
                  <tr key={r.expense_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{r.expense_no || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{r.expense_date}</td>
                    <td style={{ padding: '0.5rem' }}>{catName}</td>
                    <td style={{ padding: '0.5rem' }}>{deptName}</td>
                    <td style={{ padding: '0.5rem' }}>{r.payee_name || '—'}</td>
                    <td style={{ padding: '0.5rem' }}>{r.payment_mode || '—'}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmtMoney(r.total_amount)}</td>
                    <td style={{ padding: '0.5rem' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, background: statusColor(r.status) + '22', color: statusColor(r.status), fontSize: '0.72rem', fontWeight: 600 }}>{r.status}</span>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default PmsExpenseDashboard;
